import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import LHSectionHeader from '../../components/LHSectionHeader';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import SignaturePad from '../../components/SignaturePad';
import SignatureDisclaimer from '../../components/SignatureDisclaimer';

// Import the actions we'll need to update the signatures
import {
  setManagerSignature,
  setEmployeeSignature,
  resetManagerSignatureFlag,
} from '../../store/slices/pulseCheckSlice';
import FontExampleComponent from '../../components/FontExampleComponent';
import FontFamily from '../../constants/fonts';
import { useTheme } from '../../theme';
import type { ThemeColors } from '../../theme/types';

type SignatureType = 'employee' | 'manager';

const PulseCheckSignature: React.FC = () => {
  const dispatch = useDispatch();
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const styles = useMemo(() => createStyles(colors), [colors]);
  const {
    managerSignatureFileName,
    managerSignatureImageUrl,
    employeeSignatureFileName,
    employeeSignatureImageUrl,
    pulseState,
  } = useSelector((state: RootState) => state.pulseCheck);

  // Check screen width for responsive layout
  const windowWidth = Dimensions.get('window').width;
  const isIOS = Platform.OS === 'ios';
  const isMobile = windowWidth < 768 || isIOS;

  // State to track which signature is currently being captured
  const [activeSignatureType, setActiveSignatureType] =
    useState<SignatureType>('employee');

  // Determine if component is disabled based on pulse state
  const isDisabled = pulseState === 'completed';

  // Only show image URL if there's an actual signature file after upload
  const showManagerImageUrl = managerSignatureFileName
    ? managerSignatureImageUrl || managerSignatureFileName
    : undefined;

  const showEmployeeImageUrl = employeeSignatureFileName
    ? employeeSignatureImageUrl || employeeSignatureFileName
    : undefined;

  console.log('PulseCheckSignature: signature data from state:', {
    managerSignatureFileName,
    managerSignatureImageUrl,
    employeeSignatureFileName,
    employeeSignatureImageUrl,
    showManagerImageUrl,
    showEmployeeImageUrl,
  });

  // Function to handle clearing manager signature data from Redux
  const handleClearManagerSignature = useCallback(() => {
    console.log(
      'PulseCheckSignature: Clearing manager signature data from Redux'
    );
    dispatch(
      setManagerSignature({
        fileName: null,
        imageUrl: null,
        isNewUpload: false,
      })
    );
    dispatch(resetManagerSignatureFlag());
  }, [dispatch]);

  // Function to handle clearing employee signature data from Redux
  const handleClearEmployeeSignature = useCallback(() => {
    console.log(
      'PulseCheckSignature: Clearing employee signature data from Redux'
    );
    dispatch(
      setEmployeeSignature({
        fileName: null,
        imageUrl: null,
        isNewUpload: false,
      })
    );
  }, [dispatch]);

  // Handle signature capture based on active type
  const handleSignatureCapture = useCallback(
    (fileName: string, imageUrl?: string) => {
      if (activeSignatureType === 'employee') {
        console.log(
          'PulseCheckSignature: Received employee signature data - fileName:',
          fileName,
          'imageUrl:',
          imageUrl
        );
        dispatch(
          setEmployeeSignature({
            fileName,
            imageUrl,
            isNewUpload: true,
          })
        );
        console.log(
          'PulseCheckSignature: Dispatched setEmployeeSignature action with isNewUpload=true'
        );

        // Verify state after dispatch
        setTimeout(() => {
          const state = require('../../store').default.getState();
          console.log(
            'PulseCheckSignature: Redux state after dispatch -',
            'employeeSignatureFileName:',
            state.pulseCheck.employeeSignatureFileName,
            'employeeSignatureImageUrl:',
            state.pulseCheck.employeeSignatureImageUrl,
            'isEmployeeSignatureUpdated:',
            state.pulseCheck.isEmployeeSignatureUpdated
          );
        }, 100);
      } else {
        console.log(
          'PulseCheckSignature: Received manager signature data - fileName:',
          fileName,
          'imageUrl:',
          imageUrl
        );
        dispatch(
          setManagerSignature({
            fileName,
            imageUrl,
            isNewUpload: true,
          })
        );
        console.log(
          'PulseCheckSignature: Dispatched setManagerSignature action with isNewUpload=true'
        );

        // Verify state after dispatch
        setTimeout(() => {
          const state = require('../../store').default.getState();
          console.log(
            'PulseCheckSignature: Redux state after dispatch -',
            'managerSignatureFileName:',
            state.pulseCheck.managerSignatureFileName,
            'managerSignatureImageUrl:',
            state.pulseCheck.managerSignatureImageUrl,
            'isManagerSignatureUpdated:',
            state.pulseCheck.isManagerSignatureUpdated
          );
        }, 100);
      }
    },
    [dispatch, activeSignatureType]
  );

  // Handle signature clear based on active type
  const handleSignatureClear = useCallback(() => {
    if (activeSignatureType === 'employee') {
      handleClearEmployeeSignature();
    } else {
      handleClearManagerSignature();
    }
  }, [
    activeSignatureType,
    handleClearEmployeeSignature,
    handleClearManagerSignature,
  ]);

  // New: Separate handlers so both signature pads work independently
  const handleEmployeeSignatureCapture = useCallback(
    (fileName: string, imageUrl?: string) => {
      console.log(
        'PulseCheckSignature: Received employee signature data - fileName:',
        fileName,
        'imageUrl:',
        imageUrl
      );
      dispatch(
        setEmployeeSignature({
          fileName,
          imageUrl,
          isNewUpload: true,
        })
      );

      // Verify state after dispatch
      setTimeout(() => {
        const state = require('../../store').default.getState();
        console.log(
          'PulseCheckSignature: Redux state after dispatch -',
          'employeeSignatureFileName:',
          state.pulseCheck.employeeSignatureFileName,
          'employeeSignatureImageUrl:',
          state.pulseCheck.employeeSignatureImageUrl,
          'isEmployeeSignatureUpdated:',
          state.pulseCheck.isEmployeeSignatureUpdated
        );
      }, 100);
    },
    [dispatch]
  );

  const handleManagerSignatureCapture = useCallback(
    (fileName: string, imageUrl?: string) => {
      console.log(
        'PulseCheckSignature: Received manager signature data - fileName:',
        fileName,
        'imageUrl:',
        imageUrl
      );
      dispatch(
        setManagerSignature({
          fileName,
          imageUrl,
          isNewUpload: true,
        })
      );
      dispatch(resetManagerSignatureFlag());

      // Verify state after dispatch
      setTimeout(() => {
        const state = require('../../store').default.getState();
        console.log(
          'PulseCheckSignature: Redux state after dispatch -',
          'managerSignatureFileName:',
          state.pulseCheck.managerSignatureFileName,
          'managerSignatureImageUrl:',
          state.pulseCheck.managerSignatureImageUrl,
          'isManagerSignatureUpdated:',
          state.pulseCheck.isManagerSignatureUpdated
        );
      }, 100);
    },
    [dispatch]
  );

  // Get current signature data based on active type
  const getCurrentSignatureData = () => {
    if (activeSignatureType === 'employee') {
      return {
        fileName: employeeSignatureFileName,
        imageUrl: showEmployeeImageUrl,
        title: 'Employee Signature',
      };
    } else {
      return {
        fileName: managerSignatureFileName,
        imageUrl: showManagerImageUrl,
        title: 'Manager Signature',
      };
    }
  };

  const currentSignature = getCurrentSignatureData();

  return (
    <View style={styles.cardContainer}>
      <LHSectionHeader
        title="Signatures"
        containerStyle={styles.header}
        textStyle={styles.sectionHeaderText}

      />

      {/* Stacked Employee & Manager Signature Pads */}
      <View style={{ backgroundColor: colors.background.surface }}>
        <View style={styles.signaturePadWrapper}>
          {isMobile ? (
            // Mobile: show a single native SignaturePad and toggle between Employee/Manager to avoid native conflicts
            <>
              <View style={styles.toggleContainer}>
                <TouchableOpacity
                  style={[
                    styles.toggleButton,
                    activeSignatureType === "employee" &&
                      styles.activeToggleButton,
                  ]}
                  onPress={() => setActiveSignatureType("employee")}
                >
                  <Text
                    style={[
                      styles.toggleButtonText,
                      activeSignatureType === "employee" &&
                        styles.activeToggleButtonText,
                    ]}
                  >
                    Employee
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.toggleButton,
                    activeSignatureType === "manager" &&
                      styles.activeToggleButton,
                  ]}
                  onPress={() => setActiveSignatureType("manager")}
                >
                  <Text
                    style={[
                      styles.toggleButtonText,
                      activeSignatureType === "manager" &&
                        styles.activeToggleButtonText,
                    ]}
                  >
                    Manager
                  </Text>
                </TouchableOpacity>
              </View>

              <View style={styles.padColumn}>
                <Text style={styles.padSectionTitle}>
                  {activeSignatureType === "employee" ? "Employee" : "Manager"}
                </Text>
                <View
                  style={[
                    styles.signaturePadContainer,
                    styles.signaturePadContainerMobile,
                  ]}
                >
                  <SignaturePad
                    key={`mobile-signature-${activeSignatureType}`}
                    containerName="observations"
                    onOK={
                      activeSignatureType === "employee"
                        ? handleEmployeeSignatureCapture
                        : handleManagerSignatureCapture
                    }
                    onClear={
                      activeSignatureType === "employee"
                        ? handleClearEmployeeSignature
                        : handleClearManagerSignature
                    }
                    initialImageUrl={
                      activeSignatureType === "employee"
                        ? showEmployeeImageUrl
                        : showManagerImageUrl
                    }
                    disabled={isDisabled}
                    isDarkTheme={isDarkTheme}
                  />

                {/* Show disclaimer for employee signature only */}
                  {activeSignatureType === "employee" && (
                    <SignatureDisclaimer disabled={isDisabled} />
                  )}
                  {/* show uploaded statuses below the pad */}
                  {employeeSignatureFileName && (
                    <Text style={styles.signatureText}>
                      Employee Signature uploaded
                    </Text>
                  )}
                  {managerSignatureFileName && (
                    <Text style={styles.signatureText}>
                      Manager Signature uploaded
                    </Text>
                  )}
                </View>
              </View>
            </>
          ) : (
            // Desktop / Web: place pads side by side
            <View style={styles.signatureRow}>
              <View style={styles.padColumn}>
                <Text style={styles.padSectionTitle}>Employee</Text>
                <View
                  style={[
                    styles.signaturePadContainer,
                    isMobile && styles.signaturePadContainerMobile,
                  ]}
                >
                  <SignaturePad
                    key="employee-signature-desktop"
                    containerName="observations"
                    onOK={handleEmployeeSignatureCapture}
                    onClear={handleClearEmployeeSignature}
                    initialImageUrl={showEmployeeImageUrl}
                    disabled={isDisabled}
                    isDarkTheme={isDarkTheme}
                  />
                  {employeeSignatureFileName && (
                    <Text style={styles.signatureText}>
                      Employee Signature uploaded
                    </Text>
                  )}

                  {/* Disclaimer for employee signature */}
                  <SignatureDisclaimer disabled={isDisabled} />
                </View>
              </View>

              <View style={styles.verticalDivider} />

              <View style={styles.padColumn}>
                <Text style={styles.padSectionTitle}>Manager</Text>
                <View
                  style={[
                    styles.signaturePadContainer,
                    isMobile && styles.signaturePadContainerMobile,
                  ]}
                >
                  <SignaturePad
                    key="manager-signature-desktop"
                    containerName="observations"
                    onOK={handleManagerSignatureCapture}
                    onClear={handleClearManagerSignature}
                    initialImageUrl={showManagerImageUrl}
                    disabled={isDisabled}
                    isDarkTheme={isDarkTheme}
                  />
                  {managerSignatureFileName && (
                    <Text style={styles.signatureText}>
                      Manager Signature uploaded
                    </Text>
                  )}
                </View>
              </View>
            </View>
          )}

          {/* Status Summary (unchanged) */}
          <View style={styles.statusContainer}>
            <View style={{ alignItems: "center" }}>
              <View style={styles.statusRow}>
                <Text style={styles.statusLabel}>Employee:</Text>
                <Text
                  style={[
                    styles.statusValue,
                    employeeSignatureFileName
                      ? styles.statusComplete
                      : styles.statusPending,
                  ]}
                >
                  {employeeSignatureFileName ? 'Signed' : 'Pending'}
                </Text>
              </View>
              <View style={styles.statusRow}>
                <Text style={styles.statusLabel}>Manager:</Text>
                <Text
                  style={[
                    styles.statusValue,
                    managerSignatureFileName
                      ? styles.statusComplete
                      : styles.statusPending,
                  ]}
                >
                  {managerSignatureFileName ? 'Signed' : 'Pending'}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    cardContainer: {
      marginBottom: 24,
      borderRadius: 6,
      overflow: "hidden",
      elevation: 4,
      shadowColor: "rgba(0, 94, 192, 0.08)",
      shadowOffset: {
        width: 4,
        height: 0,
      },
      shadowOpacity: 1,
      shadowRadius: 24,
    },
    header: {
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    sectionHeaderText: {
      fontSize: 16,
      fontWeight: '500',
      color: 'white',
    },
    toggleContainer: {
      flexDirection: 'row',
      backgroundColor: colors.background.surface,
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6,
      marginBottom: 0,
      marginTop: 0,
      paddingVertical: 16,
      paddingHorizontal: 16,
    },
    toggleButton: {
      paddingVertical: 4,
      paddingHorizontal: 8,
      backgroundColor: 'transparent',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 16,
      borderBottomWidth: 0,
      position: 'relative',
    },
    activeToggleButton: {
      backgroundColor: 'transparent',
      borderBottomWidth: 2,
      borderBottomColor: colors.brand.primary,
    },
    toggleButtonText: {
      fontFamily: FontFamily.regular,
      fontSize: 14,
      lineHeight: 20,
      fontWeight: '700',
      color: colors.text.secondary,
    },
    activeToggleButtonText: {
      fontWeight: '700',
      color: colors.text.primary,
    },
    signatureIndicator: {
      position: 'absolute',
      top: 0,
      right: -8,
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: colors.status.success,
    },
    signaturePadWrapper: {
      marginBottom: 0, // Reduce this since we're adding space to statusContainer
      backgroundColor: colors.background.surface,
      padding: 16,
    },
    signatureTitle: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 10,
      color: colors.text.primary,
      textAlign: 'center',
    },
    signaturePadContainer: {
      padding: 10,
      width: "100%",
    },
    signaturePadContainerMobile: {
      minHeight: Platform.OS === 'ios' ? 250 : 200,
      padding: 6,
    },
    padSectionTitle: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text.primary,
      marginBottom: 8,
    },
    divider: {
      height: 1,
      backgroundColor: colors.border.divider,
      marginVertical: 12,
    },
    signatureText: {
      marginTop: 8,
      fontSize: 14,
      color: colors.text.primary,
      fontWeight: '500',
      textAlign: 'center',
    },
    statusContainer: {
      backgroundColor: colors.background.surface,
      borderRadius: 6,
      padding: 12,
      marginTop: 20, // Add space between signature pad and status section
      borderTopWidth: 8,
      borderTopColor: colors.border.divider, // Divider to separate sections
      paddingTop: 16,
    },
    statusRow: {
      flexDirection: 'row',
      justifyContent: 'flex-start', // Change to space between to align labels and values
      alignItems: 'center',
      marginBottom: 6,
      width: "96%", // Set a fixed width to ensure consistent alignment
      marginLeft: 20, // Add left margin to center the status rows
    },
    statusLabel: {
      fontSize: Platform.OS === 'web' ? 14 : 12,
      fontWeight: '500',
      color: colors.text.primary,
      width: 100, // Set fixed width to ensure labels align
    },
    statusValue: {
      fontSize: Platform.OS === 'web' ? 14 : 12,
      fontWeight: '600',
    },
    statusComplete: {
      color: colors.status.success,
    },
    statusPending: {
      color: colors.status.warning,
    },
    signatureRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
    },
    padColumn: {
      flex: 1,
      paddingHorizontal: 12,
      // On wide screens force equal columns so the divider sits exactly between them
      maxWidth: Platform.OS === "web" ? "48%" : undefined,
    },
    verticalDivider: {
      width: 1,
      backgroundColor: colors.border.divider,
      marginLeft: 12,
      marginRight: Platform.OS === "web" ? 100 : 12,
      alignSelf: "stretch",
    },
  });

export default PulseCheckSignature;
